import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  X,
  Building2,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  ChevronRight,
} from 'lucide-react';
import SupplierModal from './SupplierModal';

function formatCNPJ(cnpj) {
  if (!cnpj) return '—';
  const numbers = String(cnpj).replace(/\D/g, '');
  if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cnpj;
}

const STATUS_LABEL = {
  aprovado: 'Aprovado',
  pendente: 'Pendente',
  pendente_aprovacao: 'Pendente aprovação',
  pendente_complementacao: 'Pendente complementação',
  pendente_homologacao: 'Pendente homologação',
  rejeitado: 'Rejeitado',
  suspenso: 'Suspenso',
};

function labelStatus(status) {
  if (!status) return '—';
  return STATUS_LABEL[status] || status;
}

const ProdutoFornecedoresModal = ({ isOpen, onClose, produto, empresaId }) => {
  const { hasPermission, getEmpresaId } = useAuth();
  const podeEditarFornecedor = hasPermission('manage_suppliers');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedorDetalhe, setFornecedorDetalhe] = useState(null);
  const [salvandoFornecedor, setSalvandoFornecedor] = useState(false);
  const [erroFornecedor, setErroFornecedor] = useState('');
  const [sucessoFornecedor, setSucessoFornecedor] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFornecedorDetalhe(null);
      setErroFornecedor('');
      setSucessoFornecedor('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !produto || !empresaId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setFornecedores([]);

      try {
        const getSuppliersByProduct = httpsCallable(functions, 'getSuppliersByProduct');
        const result = await getSuppliersByProduct({
          produto_id: produto.id,
          produto_base_id: produto.produto_base_id || produto.codigo_origem,
          codigo: produto.codigo || produto.codigo_produto || produto.codigo_origem,
          codigo_produto: produto.codigo_produto || produto.codigo_origem,
          id_empresa: empresaId,
        });

        if (cancelled) return;

        if (!result.data?.success) {
          throw new Error(result.data?.message || 'Não foi possível carregar fornecedores.');
        }

        const list = result.data?.data?.suppliers || [];
        const sorted = [...list].sort((a, b) =>
          (a.nome_empresa || '').localeCompare(b.nome_empresa || '', 'pt-BR', { sensitivity: 'base' })
        );
        setFornecedores(sorted);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e.message || 'Erro ao carregar fornecedores.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, produto, empresaId]);

  const handleSubmitFornecedorEdit = async (formData) => {
    const supplierId =
      fornecedorDetalhe?.id || fornecedorDetalhe?.supplier_id || fornecedorDetalhe?.uid;
    if (!supplierId) return;

    try {
      setSalvandoFornecedor(true);
      setErroFornecedor('');
      setSucessoFornecedor('');

      const updateSupplier = httpsCallable(functions, 'updateSupplier');
      const cleanFormData = {
        ...formData,
        observacoes: formData.observacoes || '',
        documentos_anexos: formData.documentos_anexos || [],
        descricao_servicos: formData.descricao_servicos || '',
        certificacoes: formData.certificacoes || [],
        capacidade_producao: formData.capacidade_producao || '',
        regioes_atendimento: formData.regioes_atendimento || [],
        endereco: {
          ...formData.endereco,
          complemento: formData.endereco?.complemento || '',
        },
      };

      const result = await updateSupplier({
        supplier_id: supplierId,
        ...cleanFormData,
        id_empresa: getEmpresaId(),
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao atualizar fornecedor');
      }

      setSucessoFornecedor('Fornecedor atualizado com sucesso!');
      setFornecedores((prev) =>
        prev.map((f) => {
          const fid = f.id || f.supplier_id || f.uid;
          if (fid !== supplierId) return f;
          return { ...f, ...cleanFormData, id: supplierId };
        })
      );
      setFornecedorDetalhe((prev) =>
        prev ? { ...prev, ...cleanFormData, id: supplierId } : prev
      );
    } catch (e) {
      console.error(e);
      setErroFornecedor(e.message || 'Erro ao atualizar fornecedor');
    } finally {
      setSalvandoFornecedor(false);
    }
  };

  if (!isOpen) return null;

  const nomeInsumo = produto?.nome || produto?.nome_produto || 'Insumo';

  if (fornecedorDetalhe) {
    const modoFornecedor = podeEditarFornecedor ? 'edit' : 'view';
    return (
      <SupplierModal
        isOpen
        mode={modoFornecedor}
        initialData={{
          ...fornecedorDetalhe,
          id: fornecedorDetalhe.id || fornecedorDetalhe.supplier_id || fornecedorDetalhe.uid,
        }}
        onClose={() => {
          setFornecedorDetalhe(null);
          setErroFornecedor('');
          setSucessoFornecedor('');
        }}
        onSubmit={podeEditarFornecedor ? handleSubmitFornecedorEdit : () => {}}
        loading={salvandoFornecedor}
        error={erroFornecedor}
        success={sucessoFornecedor}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-labelledby="fornecedores-insumo-title"
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div className="min-w-0 pr-3">
            <h2 id="fornecedores-insumo-title" className="text-lg font-semibold text-gray-900">
              Fornecedores
            </h2>
            <p className="mt-0.5 truncate text-sm text-gray-600" title={nomeInsumo}>
              {nomeInsumo}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
              <Loader2 className="mb-2 h-8 w-8 animate-spin text-blue-500" />
              <span className="text-sm">Carregando fornecedores…</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && fornecedores.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">
              Nenhum fornecedor associado a este insumo.
            </p>
          )}

          {!loading && !error && fornecedores.length > 0 && (
            <ul className="space-y-3">
              {fornecedores.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setErroFornecedor('');
                      setSucessoFornecedor('');
                      setFornecedorDetalhe(f);
                    }}
                    className="group w-full rounded-lg border border-gray-100 bg-gray-50/80 p-3 text-left transition-colors hover:border-indigo-200 hover:bg-indigo-50/60 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                  >
                    <div className="flex items-start gap-2">
                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 group-hover:text-indigo-700" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 group-hover:text-indigo-900">
                          {f.nome_empresa || '—'}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          CNPJ: {formatCNPJ(f.cnpj)}
                          {f.status ? (
                            <>
                              {' '}
                              · {labelStatus(f.status)}
                            </>
                          ) : null}
                        </p>
                        {f.email && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">{f.email}</span>
                          </p>
                        )}
                        {f.telefone && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
                            <Phone className="h-3 w-3 shrink-0" />
                            <span>{f.telefone}</span>
                          </p>
                        )}
                        <p className="mt-2 text-xs font-medium text-indigo-600">
                          {podeEditarFornecedor
                            ? 'Editar cadastro do fornecedor'
                            : 'Ver cadastro do fornecedor'}
                        </p>
                      </div>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400 group-hover:text-indigo-600" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProdutoFornecedoresModal;
